// Gift/auction admin adapter (module-local, doesn't touch adapters.ts). Backend:
// MyTelegram.AdminServer's GiftsController, reading/writing the "stargiftcatalog"/
// "stargiftmodels"/"stargiftauctions"/"stargiftauctionbids" Mongo collections directly (plain
// admin-managed reference data, not EventFlow-backed - see GiftCatalogEntry's own doc comment).
import { api } from '@/lib/api'
import type { FetchParams, FetchResult } from '@/components/DataTable/types'

export interface AdminGiftRow {
  gift_id: number
  title: string
  document_id: number
  stars: number
  convert_stars: number
  upgrade_stars: number
  limited: boolean
  availability_total: number | null
  availability_remains: number | null
  model_count: number
  has_active_auction: boolean
}

export async function fetchGifts(params: FetchParams): Promise<FetchResult<AdminGiftRow>> {
  const data = await api.get<{ gifts: AdminGiftRow[] }>('/admin/gifts', { signal: params.signal })
  const rows = params.search
    ? data.gifts.filter((g) => g.title.toLowerCase().includes(params.search.toLowerCase()))
    : data.gifts
  return { rows }
}

export interface AdminStickerSet {
  sticker_set_id: number
  title: string
  short_name: string
  document_ids: number[]
}

export async function fetchStickerSets(q: string, signal?: AbortSignal): Promise<AdminStickerSet[]> {
  const data = await api.get<{ sticker_sets: AdminStickerSet[] }>('/admin/gifts/sticker-sets', {
    query: { q: q || undefined },
    signal,
  })
  return data.sticker_sets
}

export interface CreateGiftRequest {
  title: string
  document_id: number
  stars: number
  convert_stars: number
  upgrade_stars: number
  limited: boolean
  availability_total: number | null
}

export async function createGift(request: CreateGiftRequest): Promise<AdminGiftRow> {
  return api.post<AdminGiftRow>('/admin/gifts', request)
}

export interface AdminGiftAuction {
  gift_id: number
  gift_title: string
  min_bid_amount: number
  start_date: number
  end_date: number
  gifts_available: number
  gifts_left: number
  version: number
  status: 'active' | 'finished'
  average_price: number | null
  top_bid_amount: number
}

export async function fetchAuctions(signal?: AbortSignal): Promise<AdminGiftAuction[]> {
  const data = await api.get<{ auctions: AdminGiftAuction[] }>('/admin/gifts/auctions', signal ? { signal } : {})
  return data.auctions
}

export interface LaunchAuctionRequest {
  min_bid_amount: number
  gifts_available: number
  duration_seconds: number
}

export async function launchAuction(giftId: number, request: LaunchAuctionRequest): Promise<AdminGiftAuction> {
  return api.post<AdminGiftAuction>(`/admin/gifts/${giftId}/auctions`, request)
}
